import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, X } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const CATEGORIES = [
  { slug: "jackets", label: "JACKETS" },
  { slug: "tees", label: "TEES" },
  { slug: "jeans", label: "JEANS" },
  { slug: "tote-bags", label: "TOTES" },
  { slug: "thrifted", label: "THRIFTED" },
];

export default function SiteSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState({ slug: "", label: "" });
  const { refresh: refreshGlobal } = useSiteSettings();

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => setSettings(data)).catch((err) => console.warn(err));
  }, []);

  if (!settings) return <p className="font-mono-b p-6" data-testid="site-loading">LOADING...</p>;

  const setHero = (urls) => setSettings({ ...settings, hero_image: urls[0] || "" });
  const setLogo = (urls) => setSettings({ ...settings, logo: urls[0] || "" });
  const setCat = (slug, urls) => setSettings({ ...settings, category_images: { ...settings.category_images, [slug]: urls[0] || "" } });
  const addCategory = () => {
    if (!newCat.slug || !newCat.label) return toast.error("Fill both fields");
    const cats = [...(settings.categories || []), { slug: newCat.slug.toLowerCase().trim(), label: newCat.label.toUpperCase().trim() }];
    setSettings({ ...settings, categories: cats });
    setNewCat({ slug: "", label: "" });
  };
  const removeCategory = (slug) => {
    setSettings({ ...settings, categories: (settings.categories || []).filter((c) => c.slug !== slug) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/settings", {
        hero_image: settings.hero_image,
        logo: settings.logo,
        seo_title: settings.seo_title,
        seo_description: settings.seo_description,
        contact_phone: settings.contact_phone,
        contact_whatsapp: settings.contact_whatsapp,
        contact_hours: settings.contact_hours,
        categories: settings.categories,
        category_images: settings.category_images,
        dark_mode_default: !!settings.dark_mode_default,
        custom_title: settings.custom_title,
        custom_subtitle: settings.custom_subtitle,
        custom_image: settings.custom_image,
      });
      toast.success("Saved. Reload homepage to see all updates.");
      refreshGlobal?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="site-settings-panel">
      {/* LOGO */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">SHOP LOGO</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Shown in header + footer + browser tab.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-mono-b font-bold uppercase mb-2">CURRENT</p>
            {settings.logo ? <img src={settings.logo} alt="logo" className="w-32 h-32 object-cover border-2 border-black rounded-full bg-black" data-testid="logo-preview" /> : <p>None</p>}
          </div>
          <div><ImageUpload value={settings.logo ? [settings.logo] : []} onChange={setLogo} max={1} testIdPrefix="logo" /></div>
        </div>
      </div>

      {/* SEO */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">SEO · META TAGS</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Title + description shown in Google search results & social shares.</p>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">SEO TITLE (max ~60 chars)</label>
            <input data-testid="seo-title" value={settings.seo_title || ""} onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })} className="brutal-input w-full mt-1" />
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">SEO DESCRIPTION (max ~160 chars)</label>
            <textarea data-testid="seo-description" rows={3} value={settings.seo_description || ""} onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })} className="brutal-input w-full mt-1" />
          </div>
        </div>
      </div>

      {/* CUSTOM SECTION */}
      <div className="bg-white border-2 border-black brutal-shadow p-5" data-testid="custom-section-panel">
        <h3 className="font-display text-2xl">HOMEPAGE · CUSTOM SECTION</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Shown between "Latest drops" and the thrift callout on the homepage.</p>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">TITLE</label>
            <input
              data-testid="custom-title-input"
              value={settings.custom_title || ""}
              onChange={(e) => setSettings({ ...settings, custom_title: e.target.value })}
              placeholder="CUSTOM PIECES"
              className="brutal-input w-full mt-1"
            />
            <label className="text-xs font-mono-b font-bold uppercase mt-3 block">SUBTITLE</label>
            <textarea
              data-testid="custom-subtitle-input"
              rows={3}
              value={settings.custom_subtitle || ""}
              onChange={(e) => setSettings({ ...settings, custom_subtitle: e.target.value })}
              className="brutal-input w-full mt-1"
              placeholder="One-of-one hand-painted jackets, screen-printed tees..."
            />
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">CUSTOM SECTION IMAGE</label>
            <ImageUpload
              value={settings.custom_image ? [settings.custom_image] : []}
              onChange={(urls) => setSettings({ ...settings, custom_image: urls[0] || "" })}
              max={1}
              testIdPrefix="custom-image"
            />
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">CUSTOMER SUPPORT CONTACT</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Shown in the footer + used for WhatsApp support button.</p>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <div><label className="text-xs font-mono-b font-bold uppercase">PHONE</label><input data-testid="contact-phone" value={settings.contact_phone || ""} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="brutal-input w-full mt-1" /></div>
          <div><label className="text-xs font-mono-b font-bold uppercase">WHATSAPP (E.164)</label><input data-testid="contact-whatsapp" value={settings.contact_whatsapp || ""} onChange={(e) => setSettings({ ...settings, contact_whatsapp: e.target.value })} placeholder="+919152763208" className="brutal-input w-full mt-1" /></div>
          <div><label className="text-xs font-mono-b font-bold uppercase">HOURS</label><input data-testid="contact-hours" value={settings.contact_hours || ""} onChange={(e) => setSettings({ ...settings, contact_hours: e.target.value })} className="brutal-input w-full mt-1" /></div>
        </div>
      </div>

      {/* DARK MODE DEFAULT */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">DEFAULT THEME</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Applies to first-time visitors. Each user can override with the navbar toggle.</p>
        <label className="mt-3 inline-flex items-center gap-3 border-2 border-black bg-[#F4F4F0] px-4 py-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="site-dark-default"
            checked={!!settings.dark_mode_default}
            onChange={(e) => setSettings({ ...settings, dark_mode_default: e.target.checked })}
          />
          <span className="font-mono-b font-bold text-sm uppercase">Enable dark mode by default</span>
        </label>
      </div>

      {/* CATEGORIES */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">CATEGORIES</h3>
        <p className="font-mono-b text-xs text-black/60 mt-1">Add or remove categories shown in Shop + Home.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(settings.categories || []).map((c) => (
            <span key={c.slug} data-testid={`cat-chip-${c.slug}`} className="flex items-center gap-2 border-2 border-black bg-white px-3 py-1 font-mono-b font-bold text-xs uppercase">
              {c.label} <span className="text-black/50">/ {c.slug}</span>
              <button onClick={() => removeCategory(c.slug)} data-testid={`cat-remove-${c.slug}`} className="text-[#FF007F]"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <input data-testid="cat-new-slug" placeholder="slug (e.g. hoodies)" value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} className="brutal-input" />
          <input data-testid="cat-new-label" placeholder="Label (e.g. HOODIES)" value={newCat.label} onChange={(e) => setNewCat({ ...newCat, label: e.target.value })} className="brutal-input" />
          <button data-testid="cat-add" onClick={addCategory} className="bg-black text-[#D4FF00] border-2 border-black px-4 py-2 font-mono-b font-bold uppercase text-sm"><Plus className="inline w-4 h-4" /> ADD</button>
        </div>
      </div>

      {/* HERO + CATEGORY IMAGES */}
      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">HOMEPAGE HERO PHOTO</h3>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <div>{settings.hero_image && <img src={settings.hero_image} alt="hero" className="w-full aspect-[4/5] object-cover border-2 border-black" data-testid="hero-preview" />}</div>
          <div><ImageUpload value={settings.hero_image ? [settings.hero_image] : []} onChange={setHero} max={1} testIdPrefix="hero" /></div>
        </div>
      </div>

      <div className="bg-white border-2 border-black brutal-shadow p-5">
        <h3 className="font-display text-2xl">CATEGORY PHOTOS</h3>
        <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CATEGORIES.map((c) => (
            <div key={c.slug} className="border-2 border-black p-3" data-testid={`cat-setting-${c.slug}`}>
              <p className="font-display text-xl">{c.label}</p>
              {settings.category_images?.[c.slug] && <img src={settings.category_images[c.slug]} alt="" className="w-full aspect-square object-cover border-2 border-black mt-2" />}
              <div className="mt-2"><ImageUpload value={settings.category_images?.[c.slug] ? [settings.category_images[c.slug]] : []} onChange={(urls) => setCat(c.slug, urls)} max={1} testIdPrefix={`cat-${c.slug}`} /></div>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="site-save" disabled={saving} onClick={save} className="bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-8 py-3 font-mono-b font-bold uppercase disabled:opacity-50">
        {saving ? "SAVING..." : "SAVE ALL SITE SETTINGS"}
      </button>
    </div>
  );
}
